import { WorkPage, WorkSection } from '../components/work-page.tsx';
import './reports.css';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from '../components/plan-gate.tsx';
import { classes } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import {
  DEFAULT_SECTION,
  REPORT_GROUPS,
  ReportTimezone,
  defaultRangePreset,
  isSection,
  reportSectionLocked,
  reportSectionPlanName,
  resolveReportRange,
  visibleReportSections,
  type RangePreset,
  type ReportGroup,
  type Section,
} from './reports/shared.tsx';
import { ReportRangeControls, ReportScopeToggle } from './reports/range-controls.tsx';
import { ReportSectionBody } from './reports/report-body.tsx';
import ReportsHubPage from './reports/reports-hub.tsx';

/**
 * `/reports`. With the `reports_hub_routes` deployment flag on for this
 * company it is the hub of report cards, and each report opens on its own
 * route (`/reports/:section`); an old `?section=` link is sent to that route.
 * Off, it is the tabbed page every report used to share.
 */
export default function ReportsPage() {
  const { company } = useSession();
  const location = useLocation();
  if (company?.reportsHubRoutes !== true) return <ReportsPageContent />;
  const search = new URLSearchParams(location.search);
  const requested = search.get('section');
  if (requested !== null) {
    // Whatever else rode on the old link (range or paging keys) goes along;
    // only `section` became part of the path. A section that is not a report
    // goes to the hub — the hub's own URL, without the stale key.
    search.delete('section');
    const rest = search.toString();
    return (
      <Navigate
        replace
        to={{
          pathname: isSection(requested) ? `/reports/${requested}` : '/reports',
          search: rest ? `?${rest}` : '',
          hash: location.hash,
        }}
        state={location.state}
      />
    );
  }
  return <ReportsHubPage />;
}

/** Keep visited report filters and paging alive while the chooser is used. */
function RetainedReportPanel({ active, children }: { active: boolean; children: ReactNode }) {
  const lastVisible = useRef(children);
  if (active) lastVisible.current = children;
  return <>{lastVisible.current}</>;
}

/** The tabbed page: every report under one group chooser and one row of tabs. */
function ReportsPageContent() {
  const hasReports = usePlanFeature('reports_core');
  const { t } = useTranslation();
  useDocumentTitle(t('reports.documentTitle'));
  const { company, currentLocation, locations, user } = useSession();

  const location = useLocation();
  const navigate = useNavigate();
  const requestedSection = new URLSearchParams(location.search).get('section');
  const section = isSection(requestedSection) ? requestedSection : DEFAULT_SECTION;
  const setSection = (value: Section) => {
    const search = new URLSearchParams(location.search);
    search.set('section', value);
    navigate(
      { pathname: location.pathname, search: search.toString(), hash: location.hash },
      { state: location.state },
    );
  };

  const sections = useMemo(() => visibleReportSections(company, hasReports), [company, hasReports]);
  /**
   * Which section is actually showing — derived rather than trusted.
   *
   * `section` is what somebody picked, and a pick can outlive the tab it
   * names: a session refresh that lands mode-residential while Job costing is
   * open would otherwise leave the tab gone and its panel still rendering,
   * fetching a report from behind a `aria-labelledby` pointing at nothing.
   * Derived, that state simply stops being reachable, with no effect to fire
   * and no extra render.
   */
  const activeSection = sections.includes(section) ? section : hasReports ? DEFAULT_SECTION : 'money-received';
  const identity = JSON.stringify([company?.id, user?.id, currentLocation?.id]);
  const [visited, setVisited] = useState({ identity, sections: [activeSection] });
  if (visited.identity !== identity) setVisited({ identity, sections: [activeSection] });
  else if (visited.sections.at(-1) !== activeSection)
    setVisited({ identity, sections: [...visited.sections.filter((value) => value !== activeSection), activeSection] });
  const activeGroup = (Object.keys(REPORT_GROUPS) as ReportGroup[]).find((group) =>
    (REPORT_GROUPS[group] as readonly Section[]).includes(activeSection),
  )!;
  const locked = (value: Section) => reportSectionLocked(company, value);
  const groupSections = (REPORT_GROUPS[activeGroup] as readonly Section[]).filter((value) => sections.includes(value));
  const unlockedSections = groupSections.filter((value) => !locked(value));
  const lockedSections = groupSections.filter(locked);
  function reportTabs(values: readonly Section[]) {
    return (
      <div role="tablist" aria-label={t('reports.tabsAriaLabel')} className="flex flex-wrap gap-1">
        {values.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`reports-tab-${value}`}
            aria-selected={activeSection === value}
            aria-controls={`reports-panel-${value}`}
            tabIndex={activeSection === value || (!values.includes(activeSection) && value === values[0]) ? 0 : -1}
            onClick={() => setSection(value)}
            onKeyDown={(event) => {
              const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
              const next =
                event.key === 'Home'
                  ? values[0]
                  : event.key === 'End'
                    ? values.at(-1)
                    : offset
                      ? values[(values.indexOf(value) + offset + values.length) % values.length]
                      : undefined;
              if (!next) return;
              event.preventDefault();
              setSection(next);
              document.getElementById(`reports-tab-${next}`)?.focus();
            }}
            className={classes(
              'rounded-md px-3 py-2 text-sm font-medium',
              activeSection === value
                ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]',
            )}
          >
            {t(`reports.tab.${value}`)}
            {locked(value) ? <span className="ml-1.5 badge">{reportSectionPlanName(value)}</span> : null}
          </button>
        ))}
      </div>
    );
  }

  const hasScopes = locations.length >= 2;
  const [scopeChoice, setScopeChoice] = useState<'location' | 'all'>('location');
  const scope = hasScopes && scopeChoice === 'all' ? ('all' as const) : undefined;

  // The range somebody picked, once they have picked one. Until then each tab
  // opens on its own default.
  const [chosenPreset, setPreset] = useState<RangePreset | null>(null);
  const preset: RangePreset = chosenPreset ?? defaultRangePreset(activeSection);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const timezone = scope === 'all' ? company?.timezone : (currentLocation?.timezone ?? company?.timezone);
  const range = useMemo(
    () => resolveReportRange(preset, customFrom, customTo, timezone),
    [preset, customFrom, customTo, timezone],
  );

  return (
    <ReportTimezone.Provider value={timezone ?? 'UTC'}>
      <WorkPage
        title={t('reports.title')}
        description={t('reports.description')}
        actions={<ReportScopeToggle choice={scopeChoice} onChange={setScopeChoice} />}
      >
        <WorkSection title={t('reports.groupsLabel')}>
          <nav aria-label={t('reports.groupsLabel')} className="mb-6 space-y-3">
            <div className="report-group-controls">
              {(Object.keys(REPORT_GROUPS) as ReportGroup[])
                .filter((group) => REPORT_GROUPS[group].some((value) => sections.includes(value)))
                .map((group) => (
                  <button
                    key={group}
                    type="button"
                    aria-pressed={group === activeGroup}
                    className={classes(
                      'rounded-md border px-4 py-3 text-left font-semibold',
                      group === activeGroup
                        ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                        : 'border-[color:var(--color-line)]',
                    )}
                    onClick={() => {
                      if (group === activeGroup) return;
                      const available = (REPORT_GROUPS[group] as readonly Section[]).filter((value) =>
                        sections.includes(value),
                      );
                      const previous = [...visited.sections].reverse().find((value) => available.includes(value));
                      setSection(previous ?? available.find((value) => !locked(value)) ?? available[0]!);
                    }}
                  >
                    {t(`reports.groups.${group}`)}
                  </button>
                ))}
            </div>
            {reportTabs(unlockedSections)}
            {lockedSections.length ? (
              <details key={activeGroup} open={locked(activeSection)}>
                <summary className="cursor-pointer py-2 text-sm text-[color:var(--color-muted)]">
                  {t('reports.moreReports')}
                </summary>
                {reportTabs(lockedSections)}
              </details>
            ) : null}
          </nav>
        </WorkSection>

        <ReportRangeControls
          section={activeSection}
          preset={preset}
          onPreset={setPreset}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFrom={setCustomFrom}
          onCustomTo={setCustomTo}
        />

        {sections
          .filter(
            (value) => value === activeSection || (visited.identity === identity && visited.sections.includes(value)),
          )
          .map((value) => (
            <div
              key={`${identity}:${value}`}
              role="tabpanel"
              id={`reports-panel-${value}`}
              aria-label={t(`reports.tab.${value}`)}
              hidden={value !== activeSection}
            >
              <RetainedReportPanel active={value === activeSection}>
                <ReportSectionBody section={value} range={range} scope={scope} />
              </RetainedReportPanel>
            </div>
          ))}
      </WorkPage>
    </ReportTimezone.Provider>
  );
}
